import { trackError } from './analytics';

export type RecaptchaError = {
  code: 'missing_key' | 'invalid_key' | 'network_error' | 'timeout' | 'blocked' | 'load_failed' | 'execution_failed';
  message: string;
  details?: unknown;
};

export class RecaptchaVerificationError extends Error {
  constructor(public error: RecaptchaError) {
    super(error.message);
    this.name = 'RecaptchaVerificationError';
  }
}

export const handleRecaptchaError = (error: unknown): RecaptchaError => {
  console.error('ReCAPTCHA error:', error);

  // Track error for analytics
  trackError(error instanceof Error ? error : new Error('ReCAPTCHA error'), 'recaptcha');

  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return {
        code: 'network_error',
        message: 'Network error occurred during verification',
        details: error
      };
    }

    // Timeout errors
    if (error.message.includes('timeout')) {
      return {
        code: 'timeout',
        message: 'Verification timed out',
        details: error
      };
    }

    // Blocked or rate limited
    if (error.message.includes('blocked') || error.message.includes('rate')) {
      return {
        code: 'blocked',
        message: 'Security verification is temporarily blocked',
        details: error
      };
    }

    // Script load failures
    if (error.message.includes('script') || error.message.includes('load')) {
      return {
        code: 'load_failed',
        message: 'Failed to load security verification',
        details: error
      };
    }

    // Execution failures
    if (error.message.includes('execute') || error.message.includes('grecaptcha')) {
      return {
        code: 'execution_failed',
        message: 'Security verification failed to execute',
        details: error
      };
    }

    // Configuration errors
    if (error.message.includes('VITE_SUPABASE_URL')) {
      return {
        code: 'missing_key',
        message: 'reCAPTCHA is not properly configured. Please check your environment variables.',
        details: error
      };
    }

    if (error.message.includes('Invalid URL')) {
      return {
        code: 'invalid_key',
        message: 'The reCAPTCHA site key appears to be invalid. Please check your configuration.',
        details: error
      };
    }
  }

  // Default error
  return {
    code: 'execution_failed',
    message: 'Security verification failed',
    details: error
  };
};